import {
  FLORA_CATEGORIES,
  type FloraDefinition,
  type FloraCategory,
  type SunlightRequirement,
  type MoistureRequirement,
  type GrowthCurve,
  type FloraGeometryForm,
} from './FloraDefinition';
import {
  FLORAL_MORPHOLOGIES,
  FOOD_RESOURCE_KINDS,
  HABITAT_RESOURCE_KINDS,
  WILDLIFE_GROUPS,
  type EcologyPhase,
  type FloralMorphology,
  type WildlifeGroup,
} from './FloraEcology';

/** Result of validating a candidate flora record. */
export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

const SUNLIGHT: readonly SunlightRequirement[] = ['full-sun', 'part-sun', 'part-shade', 'full-shade'];
const MOISTURE: readonly MoistureRequirement[] = ['dry', 'average', 'moist', 'wet'];
const GROWTH_CURVES: readonly GrowthCurve[] = ['linear', 'ease-out', 'logistic'];
const GEOMETRY_FORMS: readonly FloraGeometryForm[] = [
  'round-canopy-tree',
  'conical-tree',
  'mounding-shrub',
  'grass-tuft',
  'perennial-clump',
  'groundcover-mat',
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function checkString(errors: string[], obj: Record<string, unknown>, key: string, path: string): void {
  if (typeof obj[key] !== 'string' || obj[key].length === 0) {
    errors.push(`${path}.${key} must be a non-empty string`);
  }
}

function checkNumber(errors: string[], obj: Record<string, unknown>, key: string, path: string): void {
  if (typeof obj[key] !== 'number' || !Number.isFinite(obj[key])) {
    errors.push(`${path}.${key} must be a finite number`);
  }
}

function checkRange(errors: string[], value: unknown, path: string): void {
  if (!isRecord(value)) {
    errors.push(`${path} must be an object with minimum/typical/maximum`);
    return;
  }
  checkNumber(errors, value, 'minimum', path);
  checkNumber(errors, value, 'typical', path);
  checkNumber(errors, value, 'maximum', path);
  const { minimum, typical, maximum } = value as Record<string, number>;
  if (
    typeof minimum === 'number' &&
    typeof typical === 'number' &&
    typeof maximum === 'number' &&
    !(minimum <= typical && typical <= maximum)
  ) {
    errors.push(`${path} must satisfy minimum ≤ typical ≤ maximum`);
  }
}

function checkSeasonalAppearance(errors: string[], value: unknown, path: string): void {
  if (!isRecord(value)) {
    errors.push(`${path} must be a seasonal appearance object`);
    return;
  }
  checkString(errors, value, 'foliageColor', path);
  checkNumber(errors, value, 'foliageDensity', path);
  checkNumber(errors, value, 'branchVisibility', path);
  if (typeof value.dormant !== 'boolean') {
    errors.push(`${path}.dormant must be a boolean`);
  }
}

/**
 * Validate a candidate flora record. Returns field-specific, human-readable errors (spec §20). The
 * `schemaVersion` is required. Does not throw — see {@link assertValidFloraDefinition}.
 */
export function validateFloraDefinition(candidate: unknown): ValidationResult {
  const errors: string[] = [];
  if (!isRecord(candidate)) {
    return { valid: false, errors: ['flora record must be an object'] };
  }

  checkString(errors, candidate, 'id', 'flora');
  checkString(errors, candidate, 'botanicalName', 'flora');

  if (typeof candidate.schemaVersion !== 'number' || !Number.isInteger(candidate.schemaVersion)) {
    errors.push('flora.schemaVersion is required and must be an integer');
  }

  if (
    !Array.isArray(candidate.commonNames) ||
    candidate.commonNames.some((n) => typeof n !== 'string')
  ) {
    errors.push('flora.commonNames must be an array of strings');
  }

  if (!Array.isArray(candidate.tags) || candidate.tags.some((t) => typeof t !== 'string')) {
    errors.push('flora.tags must be an array of strings');
  }

  if (!FLORA_CATEGORIES.includes(candidate.category as FloraCategory)) {
    errors.push(`flora.category must be one of: ${FLORA_CATEGORIES.join(', ')}`);
  }

  if (
    !Array.isArray(candidate.sunlight) ||
    candidate.sunlight.length === 0 ||
    candidate.sunlight.some((s) => !SUNLIGHT.includes(s as SunlightRequirement))
  ) {
    errors.push('flora.sunlight must be a non-empty array of valid sunlight values');
  }

  if (
    !Array.isArray(candidate.moisture) ||
    candidate.moisture.length === 0 ||
    candidate.moisture.some((m) => !MOISTURE.includes(m as MoistureRequirement))
  ) {
    errors.push('flora.moisture must be a non-empty array of valid moisture values');
  }

  // Growth
  const growth = candidate.growth;
  if (!isRecord(growth)) {
    errors.push('flora.growth is required');
  } else {
    if (!Array.isArray(growth.containerStages) || growth.containerStages.length === 0) {
      errors.push('flora.growth.containerStages must be a non-empty array');
    } else {
      growth.containerStages.forEach((stage, i) => {
        const p = `flora.growth.containerStages[${i}]`;
        if (!isRecord(stage)) {
          errors.push(`${p} must be an object`);
          return;
        }
        checkString(errors, stage, 'id', p);
        checkNumber(errors, stage, 'approximateAgeYears', p);
        checkNumber(errors, stage, 'heightMeters', p);
        checkNumber(errors, stage, 'spreadMeters', p);
      });
    }
    checkRange(errors, growth.matureHeightMeters, 'flora.growth.matureHeightMeters');
    checkRange(errors, growth.matureSpreadMeters, 'flora.growth.matureSpreadMeters');
    if (!GROWTH_CURVES.includes(growth.heightCurve as GrowthCurve)) {
      errors.push('flora.growth.heightCurve must be linear, ease-out, or logistic');
    }
    if (!GROWTH_CURVES.includes(growth.spreadCurve as GrowthCurve)) {
      errors.push('flora.growth.spreadCurve must be linear, ease-out, or logistic');
    }
  }

  // Seasons
  const seasons = candidate.seasons;
  if (!isRecord(seasons)) {
    errors.push('flora.seasons is required');
  } else {
    if (typeof seasons.evergreen !== 'boolean') {
      errors.push('flora.seasons.evergreen must be a boolean');
    }
    for (const s of ['spring', 'summer', 'autumn', 'winter']) {
      checkSeasonalAppearance(errors, seasons[s], `flora.seasons.${s}`);
    }
  }

  // Rendering
  const rendering = candidate.rendering;
  if (!isRecord(rendering)) {
    errors.push('flora.rendering is required');
  } else {
    if (!GEOMETRY_FORMS.includes(rendering.geometryForm as FloraGeometryForm)) {
      errors.push(`flora.rendering.geometryForm must be one of: ${GEOMETRY_FORMS.join(', ')}`);
    }
    if (
      !Array.isArray(rendering.lodDistancesMeters) ||
      rendering.lodDistancesMeters.length !== 2 ||
      rendering.lodDistancesMeters.some((d) => typeof d !== 'number')
    ) {
      errors.push('flora.rendering.lodDistancesMeters must be a [number, number] tuple');
    }
    checkNumber(errors, rendering, 'impostorDistanceMeters', 'flora.rendering');
  }

  // Placement
  const placement = candidate.placement;
  if (!isRecord(placement)) {
    errors.push('flora.placement is required');
  } else {
    checkNumber(errors, placement, 'footprintRadiusMeters', 'flora.placement');
    checkNumber(errors, placement, 'recommendedSpacingMeters', 'flora.placement');
    checkNumber(errors, placement, 'maxSlopeDegrees', 'flora.placement');
  }

  // Schema 2 blocks. All optional — a record that omits them is a valid schema 1 record, and the
  // resolver treats every absence as "not described yet" rather than as an error. What is checked is
  // that a block which *is* present is well-formed, because a malformed climate range would silently
  // classify every site as unsuitable and a malformed ecology block would silently skew a report.
  checkClimate(errors, candidate.climate);
  checkPhenology(errors, candidate.phenology);
  checkNativeRange(errors, candidate.nativeRange);
  checkEcology(errors, candidate.ecology);

  return { valid: errors.length === 0, errors };
}

/* ------------------------------ Schema 2 blocks ------------------------------ */

/** Optional finite number in an inclusive range. Absent is always fine; present and wrong is not. */
function checkOptionalNumber(
  errors: string[],
  obj: Record<string, unknown>,
  key: string,
  path: string,
  min: number,
  max: number,
): void {
  const value = obj[key];
  if (value === undefined) return;
  if (typeof value !== 'number' || !Number.isFinite(value) || value < min || value > max) {
    errors.push(`${path}.${key} must be a number between ${min} and ${max}`);
  }
}

function checkOptionalBoolean(
  errors: string[],
  obj: Record<string, unknown>,
  key: string,
  path: string,
): void {
  if (obj[key] !== undefined && typeof obj[key] !== 'boolean') {
    errors.push(`${path}.${key} must be a boolean`);
  }
}

function checkStringArray(errors: string[], value: unknown, path: string): void {
  if (value === undefined) return;
  if (!Array.isArray(value) || value.some((entry) => typeof entry !== 'string')) {
    errors.push(`${path} must be an array of strings`);
  }
}

function checkClimate(errors: string[], value: unknown): void {
  if (value === undefined) return;
  if (!isRecord(value)) {
    errors.push('flora.climate must be an object');
    return;
  }
  const path = 'flora.climate';
  checkOptionalNumber(errors, value, 'minimumZone', path, 1, 13.5);
  checkOptionalNumber(errors, value, 'maximumZone', path, 1, 13.5);
  checkOptionalNumber(errors, value, 'maximumHeatZone', path, 1, 12);
  checkOptionalNumber(errors, value, 'minimumGrowingSeasonDays', path, 0, 365);
  checkOptionalNumber(errors, value, 'zoneToleranceSlack', path, 0, 4);
  checkOptionalBoolean(errors, value, 'droughtTolerant', path);
  checkOptionalBoolean(errors, value, 'moistureSensitive', path);

  const { minimumZone, maximumZone } = value as Record<string, number | undefined>;
  if (
    typeof minimumZone === 'number' &&
    typeof maximumZone === 'number' &&
    minimumZone > maximumZone
  ) {
    errors.push(`${path} must satisfy minimumZone ≤ maximumZone`);
  }
}

function checkPhenology(errors: string[], value: unknown): void {
  if (value === undefined) return;
  if (!isRecord(value)) {
    errors.push('flora.phenology must be an object');
    return;
  }
  const path = 'flora.phenology';
  checkOptionalNumber(errors, value, 'referenceZone', path, 1, 13.5);
  checkOptionalNumber(errors, value, 'springResponsiveness', path, 0, 1);
  checkOptionalNumber(errors, value, 'autumnResponsiveness', path, 0, 1);
  checkOptionalNumber(errors, value, 'fruitMaturationDays', path, 0, 365);
  checkOptionalNumber(errors, value, 'fruitPersistenceDays', path, 0, 365);
  checkOptionalNumber(errors, value, 'seedMaturationDays', path, 0, 365);
  checkOptionalNumber(errors, value, 'seedPersistenceDays', path, 0, 365);
  checkOptionalNumber(errors, value, 'dormancyDay', path, 1, 365);
}

function checkNativeRange(errors: string[], value: unknown): void {
  if (value === undefined) return;
  if (!isRecord(value)) {
    errors.push('flora.nativeRange must be an object');
    return;
  }
  checkStringArray(errors, value.native, 'flora.nativeRange.native');
  checkStringArray(errors, value.introduced, 'flora.nativeRange.introduced');
  checkStringArray(errors, value.invasive, 'flora.nativeRange.invasive');
  if (value.origin !== undefined && typeof value.origin !== 'string') {
    errors.push('flora.nativeRange.origin must be a string');
  }
}

const ECOLOGY_PHASES: readonly EcologyPhase[] = ['bloom', 'fruit', 'seed', 'foliage', 'year-round'];

function checkEcology(errors: string[], value: unknown): void {
  if (value === undefined) return;
  if (!isRecord(value)) {
    errors.push('flora.ecology must be an object');
    return;
  }

  checkResourceList(errors, value.food, 'flora.ecology.food', FOOD_RESOURCE_KINDS, 'consumers');
  checkResourceList(
    errors,
    value.habitat,
    'flora.ecology.habitat',
    HABITAT_RESOURCE_KINDS,
    'users',
  );

  if (value.larvalHosts !== undefined) {
    if (!Array.isArray(value.larvalHosts)) {
      errors.push('flora.ecology.larvalHosts must be an array');
    } else {
      value.larvalHosts.forEach((entry, index) => {
        const path = `flora.ecology.larvalHosts[${index}]`;
        if (!isRecord(entry)) {
          errors.push(`${path} must be an object`);
          return;
        }
        if (!WILDLIFE_GROUPS.includes(entry.group as WildlifeGroup)) {
          errors.push(`${path}.group must be one of: ${WILDLIFE_GROUPS.join(', ')}`);
        }
        checkOptionalNumber(errors, entry, 'speciesCount', path, 0, 10_000);
        checkStringArray(errors, entry.species, `${path}.species`);
      });
    }
  }

  if (
    value.floralMorphology !== undefined &&
    !FLORAL_MORPHOLOGIES.includes(value.floralMorphology as FloralMorphology)
  ) {
    errors.push(
      `flora.ecology.floralMorphology must be one of: ${FLORAL_MORPHOLOGIES.join(', ')}`,
    );
  }

  if (value.specialists !== undefined) {
    if (!Array.isArray(value.specialists)) {
      errors.push('flora.ecology.specialists must be an array');
    } else {
      value.specialists.forEach((entry, index) => {
        const path = `flora.ecology.specialists[${index}]`;
        if (!isRecord(entry)) {
          errors.push(`${path} must be an object`);
          return;
        }
        if (!WILDLIFE_GROUPS.includes(entry.group as WildlifeGroup)) {
          errors.push(`${path}.group must be one of: ${WILDLIFE_GROUPS.join(', ')}`);
        }
        if (!SPECIALIST_RESOURCES.includes(entry.resource as string)) {
          errors.push(`${path}.resource must be one of: ${SPECIALIST_RESOURCES.join(', ')}`);
        }
        checkOptionalNumber(errors, entry, 'speciesCount', path, 0, 10_000);
        checkStringArray(errors, entry.species, `${path}.species`);
      });
    }
  }

  checkOptionalBoolean(errors, value, 'keystone', 'flora.ecology');
  checkOptionalBoolean(errors, value, 'deerResistant', 'flora.ecology');
}

const SPECIALIST_RESOURCES: readonly string[] = ['pollen', 'nectar', 'foliage'];

/**
 * Food and habitat entries differ only in the name of their wildlife field and the vocabulary their
 * `kind` is drawn from, so they are checked by one function rather than by two that drift apart.
 */
function checkResourceList(
  errors: string[],
  value: unknown,
  path: string,
  kinds: readonly string[],
  wildlifeKey: 'consumers' | 'users',
): void {
  if (value === undefined) return;
  if (!Array.isArray(value)) {
    errors.push(`${path} must be an array`);
    return;
  }
  value.forEach((entry, index) => {
    const where = `${path}[${index}]`;
    if (!isRecord(entry)) {
      errors.push(`${where} must be an object`);
      return;
    }
    if (!kinds.includes(entry.kind as string)) {
      errors.push(`${where}.kind must be one of: ${kinds.join(', ')}`);
    }
    if (!ECOLOGY_PHASES.includes(entry.phase as EcologyPhase)) {
      errors.push(`${where}.phase must be one of: ${ECOLOGY_PHASES.join(', ')}`);
    }
    checkOptionalNumber(errors, entry, 'value', where, 0, 1);
    if (entry.value === undefined) errors.push(`${where}.value is required`);
    const wildlife = entry[wildlifeKey];
    if (
      !Array.isArray(wildlife) ||
      wildlife.some((group) => !WILDLIFE_GROUPS.includes(group as WildlifeGroup))
    ) {
      errors.push(`${where}.${wildlifeKey} must be an array of wildlife groups`);
    }
  });
}

/** Validate and throw a descriptive error if the record is invalid. Narrows the type on success. */
export function assertValidFloraDefinition(candidate: unknown): asserts candidate is FloraDefinition {
  const { valid, errors } = validateFloraDefinition(candidate);
  if (!valid) {
    throw new Error(`Invalid flora definition:\n- ${errors.join('\n- ')}`);
  }
}
